import { LoginPlataformaResponse } from "../shared/response/seguridad/LoginPlataformaResponse";

/** Qué opciones del menú ve el usuario, más la pestaña Usuarios del cliente, que también depende del rol.
 *  Mismo criterio que la tabla de permisos del Server (`Api/Grpc/Comun/PermisosPlataforma`): el menú es
 *  comodidad, el Server es el que hace cumplir. */
export interface MenuPlataforma {
  /** Dashboard: lo ve quien tenga al menos uno de sus recuadros (Clientes, Pipeline, Marketing, Hardware o
   *  Proyectos). Cada recuadro sigue la opción del menú de la que sale. */
  showDashboard: boolean;
  showMisProyectos: boolean;
  showClientes: boolean;
  /** Pestaña Usuarios del detalle del cliente (cuentas de la app). No es una opción del menú. */
  showUsuariosApp: boolean;
  showMapaFincas: boolean;
  showPromotores: boolean;
  showMarketing: boolean;
  showCotizaciones: boolean;
  showOrdTrabajo: boolean;
  showOrdPedido: boolean;
  showHardware: boolean;
  showMonitoreo: boolean;
  showFlota: boolean;
  showUsuarios: boolean;
  showCostos: boolean;
  showCultivos: boolean;
  showProductos: boolean;
  showProyectos: boolean;
}

/**
 * Menú según los roles del login. Técnico agrónomo (con ficha de técnico): portal restringido, solo
 * "Mis proyectos". `hasSuperAdmin` ya incluye al superusuario (lo suma el Server al armar el login).
 * Los módulos exclusivos de Sextante se ocultan en Nitrofert, también la pestaña Usuarios del cliente.
 */
export function calcularMenu(r: LoginPlataformaResponse, isSextante: boolean): MenuPlataforma {
  const isTecnico = r.tecnicoId != null;
  const isAdmin = !isTecnico && r.hasSuperAdmin;
  const admin = (v: boolean): boolean => (isTecnico ? false : v);
  const soloSextante = (v: boolean): boolean => (isSextante ? v : false);
  const showClientes = admin(isAdmin || r.hasComercial || r.hasOficina);
  const showMarketing = admin(isAdmin || r.hasComercial || r.hasOficina);
  const showCotizaciones = admin(isAdmin || r.hasComercial || r.hasOficina);
  const showHardware = soloSextante(admin(isAdmin || r.hasHardware));
  const showProyectos = admin(isAdmin);
  return {
    showDashboard:    showClientes || showMarketing || showCotizaciones || showHardware || showProyectos,
    showMisProyectos: isTecnico,
    showClientes,
    // Cuentas de la app del cliente: solo en Sextante; en Nitrofert nadie, ni el superusuario
    // (Server: PermisosPlataforma.CuentasAppCerradas).
    showUsuariosApp:  soloSextante(showClientes),
    showMapaFincas:   showClientes,
    showPromotores:   admin(isAdmin || r.hasOficina),
    showMarketing,
    showCotizaciones,
    showOrdTrabajo:   soloSextante(admin(isAdmin || r.hasComercial || r.hasPilotos || r.hasConductores || r.hasOficina)),
    showOrdPedido:    soloSextante(admin(isAdmin || r.hasComercial || r.hasHardware || r.hasOficina)),
    showHardware,
    showMonitoreo:    admin(isAdmin),
    showFlota:        soloSextante(admin(isAdmin || r.hasPilotos || r.hasConductores)),
    showUsuarios:     admin(isAdmin),
    showCostos:       admin(isAdmin),
    showCultivos:     admin(isAdmin),
    showProductos:    admin(isAdmin),
    showProyectos,
  };
}

/** Ruta de la app → opción del menú que la habilita. */
const RUTAS: Record<string, keyof MenuPlataforma> = {
  "/dashboard":                     "showDashboard",
  "/proyectos/mis-proyectos":       "showMisProyectos",
  "/terceros/clientes":             "showClientes",
  "/terceros/mapa-fincas":          "showMapaFincas",
  "/terceros/promotores":           "showPromotores",
  "/marketing/campanas":            "showMarketing",
  "/ventas/pipeline":               "showCotizaciones",
  "/sistema/hardware":              "showHardware",
  "/monitoreo/estados-fenologicos": "showMonitoreo",
  "/monitoreo/plagas":              "showMonitoreo",
  "/admin/usuarios":                "showUsuarios",
  "/admin/costos":                  "showCostos",
  "/admin/catalogos/cultivos":      "showCultivos",
  "/admin/catalogos/productos":     "showProductos",
  "/proyectos/dashboard":           "showProyectos",
  "/proyectos/proyectos":           "showProyectos",
  "/proyectos/catalogo":            "showProyectos",
  "/proyectos/parametros":          "showProyectos",
  "/proyectos/macroeconomico":      "showProyectos",
  "/proyectos/cosechas":            "showProyectos",
  "/proyectos/tecnicos":            "showProyectos",
  "/proyectos/nucleos":             "showProyectos",
  "/proyectos/macro-proyectos":     "showProyectos",
  "/proyectos/cultivos":            "showProyectos",
};

/** Si el usuario tiene al menos una opción de menú. Sin ninguna (usuario creado solo desde el portal de
 *  Nitria, sin roles) se le muestra `SinPermisosPage`. */
export function tieneOpciones(menu: MenuPlataforma): boolean {
  return Object.values(menu).some((visible) => visible);
}

/** Si el usuario puede abrir la ruta (una ruta escrita a mano en la URL no salta el menú). */
export function rutaPermitida(route: string, menu: MenuPlataforma): boolean {
  const opcion = RUTAS[route];
  return opcion !== undefined && menu[opcion];
}
